import { useEffect } from 'react'
import styled from 'styled-components'
import { Wrapper, Grid, P } from '@farewill/ui'
import { COLOR, FONT, GTR } from '@farewill/ui/tokens'
import { Formik } from 'formik'
import get from 'lodash/get'
import has from 'lodash/has'
import { Link } from 'react-router-dom'
import { dayjsLondon } from 'lib/dayjs'
import CallBox from 'components/styled/call-box'
import LiveIndicator from 'components/live-indicator'
import { AircallEventAttributes } from 'lib/models/aircall-event'
import {
  LEAD_SOURCE_IDENTIFIER_OPTIONS,
  LEAD_PARTNER_TYPES,
  LEAD_SOURCE_TYPES,
  PRODUCTS,
} from 'utils/enums'
import { getAircallContactName } from 'utils/helpers'
import Suggestions from '../containers/suggestions'
import NewContactForm from './form'
import { NewContactFormValues } from '../types'

export type NewContactFormErrors = Partial<
  Record<keyof NewContactFormValues, string>
>

const validate = (values: NewContactFormValues) => {
  const errors: NewContactFormErrors = {}
  const {
    interestedInProducts,
    sourceType,
    sourceIdentifier,
    partnerType,
    partnerId,
    brandId,
  } = values

  if (!brandId) {
    errors.brandId = 'Please select a brand.'
  }

  if (brandId && get(interestedInProducts, 'length', 0) === 0) {
    errors.interestedInProducts = 'Please select at least one product.'
  }

  if (!sourceType) {
    errors.sourceType = 'Please select a source type.'
  }

  if (sourceType === LEAD_SOURCE_TYPES.branch_referral) {
    if (!sourceIdentifier) {
      errors.sourceIdentifier = 'Compass ID is required'
    } else if (!/^\d{8}$/.test(sourceIdentifier)) {
      errors.sourceIdentifier = 'Compass ID must be exactly 8 digits'
    }
  } else if (
    has(LEAD_SOURCE_IDENTIFIER_OPTIONS, sourceType) &&
    !sourceIdentifier
  ) {
    errors.sourceIdentifier = 'Please select a source identifier.'
  }

  if (
    sourceType === LEAD_SOURCE_TYPES.external_referral &&
    partnerType === LEAD_PARTNER_TYPES.none
  ) {
    errors.partnerType = 'Please select the partner that referred this lead.'
  }

  if (partnerType !== LEAD_PARTNER_TYPES.none && !partnerId) {
    errors.partnerId = 'Please select the name of the partner'
  }

  return errors
}

const StyledBackLink = styled(Link)`
  color: ${COLOR.GREY.MEDIUM};
  font-size: ${FONT.SIZE.S};
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
`

type NewContactProps = {
  call: AircallEventAttributes
  handleSubmit: () => Promise<void>
  unsetCall: () => void
}

const NewContact = ({ call, handleSubmit, unsetCall }: NewContactProps) => {
  useEffect(() => {
    return () => {
      unsetCall()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const callSelected = call.id
  const isLive = !call.ended_at

  const initialValues: NewContactFormValues = {
    brandId: '',
    interestedInProducts: [],
    sourceType: 'inbound_call',
    sourceIdentifier: '',
    referredBy: '',
    scheduledNextCallAt: '',
    notes: '',
    partnerType: 'none',
    partnerId: '',
  }

  if (call.number) {
    const product = call.number.name.split(' ')[0].toLowerCase()

    const lineNameToProductName = {
      wills: PRODUCTS.WILL,
      probate: PRODUCTS.PROBATE,
      funeral: PRODUCTS.FUNERAL,
    }

    const isFarewillProduct = (
      product: string
    ): product is keyof typeof lineNameToProductName => {
      return product in lineNameToProductName
    }

    if (isFarewillProduct(product)) {
      initialValues.interestedInProducts.push(lineNameToProductName[product])
    }
  }

  return (
    <Grid>
      <Grid.Item span={8} padding={[GTR.L, 0, 0]}>
        {callSelected && (
          <Wrapper separator>
            <StyledBackLink to="/calls">← Back to inbound calls</StyledBackLink>
          </Wrapper>
        )}
        {callSelected && (
          <Wrapper separator>
            <CallBox live={isLive}>
              <P>
                {isLive ? (
                  <LiveIndicator>Live call </LiveIndicator>
                ) : (
                  dayjsLondon(new Date(call.started_at * 1000)).format(
                    'ddd, HH[:]mm'
                  )
                )}{' '}
                &bull {call.number.name} &bull{' '}
                {getAircallContactName(call.contact) || 'Unknown'} &bull{' '}
                {call.user?.name}{' '}
              </P>
            </CallBox>
          </Wrapper>
        )}
        <Wrapper separator>
          <Formik
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validate={validate}
          >
            <NewContactForm call={call} />
          </Formik>
        </Wrapper>
      </Grid.Item>
      <Grid.Item span={4} padding={[GTR.L, 0, 0]}>
        <Suggestions />
      </Grid.Item>
    </Grid>
  )
}

export default NewContact
